import { Link, type LinkProps } from "@tanstack/react-router";

/**
 * ChipRelacion es un elemento de la relación cruzada que se pinta en una celda: una
 * etiqueta corta (el código), un `titulo` opcional con el contexto (el LOTO, el equipo) y
 * un `enlace` opcional a la ficha correspondiente.
 */
export type ChipRelacion = {
  clave: string | number;
  etiqueta: string;
  titulo?: string;
  enlace?: LinkProps;
};

type ChipsRelacionProps = {
  items: ChipRelacion[];
  /** Texto de la celda cuando no hay ninguna relación activa. */
  vacio: string;
  /** Cuántos chips se listan antes de resumir el resto en «+n». */
  visibles?: number;
};

/** CHIPS_VISIBLES es el corte por defecto: 3 chips y el resto resumido (D3, #248). */
export const CHIPS_VISIBLES = 3;

// Estilo compartido de los chips, calcado del de la columna «Obras» de UsuariosPage.
const CHIP =
  "inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-700 text-sm";

/**
 * ChipsRelacion pinta una relación cruzada (#248) como chips: los `visibles` primeros y,
 * si sobran, un chip «+n» cuyo `title` lleva la lista COMPLETA de etiquetas. Los chips con
 * `enlace` se pintan como `<Link>` (navegación del SPA); el resto, como texto.
 *
 * La celda que lo usa debe declarar `meta.stopRowClick` para que pulsar un chip navegue en
 * vez de disparar la acción de la fila (abrir el modal de edición).
 */
export function ChipsRelacion({ items, vacio, visibles = CHIPS_VISIBLES }: ChipsRelacionProps) {
  if (items.length === 0) {
    return <span className="text-slate-500">{vacio}</span>;
  }
  const restantes = items.length - visibles;
  return (
    <div className="flex flex-wrap gap-1">
      {items.slice(0, visibles).map((item) =>
        item.enlace ? (
          <Link
            key={item.clave}
            {...item.enlace}
            title={item.titulo}
            className={`${CHIP} text-primary-700 hover:underline`}
          >
            {item.etiqueta}
          </Link>
        ) : (
          <span key={item.clave} className={CHIP} title={item.titulo}>
            {item.etiqueta}
          </span>
        ),
      )}
      {restantes > 0 ? (
        <span
          className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 font-medium text-slate-600 text-sm"
          title={items.map((i) => i.etiqueta).join(", ")}
        >
          +{restantes}
        </span>
      ) : null}
    </div>
  );
}
